// Shared building blocks for the admin panel, following
// design_guidelines.json (same system as the consumer app): group with
// space, not boxes; one container per table; quiet status tags (dot + text)
// instead of filled colored pills; segmented controls like the app's
// period tabs; no backdrop blur.

// Segmented control — same look as the app's Diario/Semanal/Mensual tabs.
// Scrolls sideways on narrow screens instead of wrapping.
export function Segmented({ options, value, onChange, testId }) {
  return (
    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
      <div className="inline-flex gap-1 p-1 bg-slate-800/70 rounded-xl" data-testid={testId}>
        {options.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`h-8 px-3.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
              value === key ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SectionHeader({ title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-4">
      <div className="min-w-0">
        <h2 className="text-lg font-semibold tracking-tight text-white">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

// Label + value, no box — the Dashboard's Ingresos/Gastos stat style.
export function Stat({ label, value, sub, dot }) {
  return (
    <div className="min-w-0">
      <p className="flex items-center gap-1.5 text-xs text-slate-500">
        {dot && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dot }} />}
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold tracking-tight text-slate-100 tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export function StatGrid({ children, cols = 'grid-cols-2 sm:grid-cols-4' }) {
  return <div className={`grid ${cols} gap-x-6 gap-y-5`}>{children}</div>;
}

const TONES = {
  green: '#34d399',
  violet: '#a78bfa',
  sky: '#38bdf8',
  amber: '#fbbf24',
  rose: '#fb7185',
  muted: '#64748b',
};

// Quiet status tag: colored dot + text, no filled pill.
export function Tag({ tone = 'muted', children, title }) {
  return (
    <span title={title} className="inline-flex items-center gap-1.5 text-xs text-slate-300 whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: TONES[tone] || TONES.muted }} />
      {children}
    </span>
  );
}

// Pagination pinned to the bottom of the viewport. `fixed`, not `sticky`:
// the app's body has overflow-x:hidden (see index.css), which makes it the
// sticky scroll container even though it never scrolls, so sticky stays
// put at the end of the page. The spacer keeps the last rows from hiding
// behind the bar. Solid bg — no blur.
export function StickyPager({ show = true, children }) {
  if (!show) return null;
  return (
    <>
      <div className="h-14" aria-hidden="true" />
      <div className="fixed bottom-0 inset-x-0 z-20 bg-[#020617] border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]">{children}</div>
      </div>
    </>
  );
}

// The one container a table gets: subtle border, faint fill, rounded.
export function TableCard({ children }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

export function Th({ children, className = '' }) {
  return <th className={`px-3 py-2 text-left text-[11px] font-medium text-slate-500 whitespace-nowrap ${className}`}>{children}</th>;
}

export function Td({ children, className = '' }) {
  return <td className={`px-3 py-1.5 ${className}`}>{children}</td>;
}

// Row separators stay extremely subtle; hover gives the row feedback.
export const trClass = 'border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors';

export function TableMessage({ colSpan, children }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-8 text-center text-sm text-slate-500">{children}</td>
    </tr>
  );
}

export function Loading() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="w-7 h-7 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
    </div>
  );
}

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('es-SV', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('es-SV', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
export const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Two-line user cell: email primary, name secondary.
export function UserCell({ email, name, userId }) {
  return (
    <div className="min-w-0">
      <p className="text-[13px] leading-tight text-slate-200 truncate">{email || <span className="font-mono text-slate-500">{userId}</span>}</p>
      {name && <p className="text-[11px] leading-tight text-slate-500 truncate">{name}</p>}
    </div>
  );
}
